import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { getUiLocale } from '../../../../helpers/i18n';

export { getDeviceTimezone } from '../../../../helpers/timezone';

/** Session clock in device local TZ, e.g. "14:30". */
export const formatSessionClock = (epoch) => moment(epoch).format('HH:mm');

/** Session date in device local TZ, e.g. "05/08/2026". */
export const formatSessionDate = (epoch) => moment(epoch).format('DD/MM/YYYY');

/** Session day label in device local TZ, e.g. "Monday, 05/08/2026". */
export const formatSessionDay = (epoch, days) =>
  days[moment(epoch).day()] + ', ' + formatSessionDate(epoch);

export const convertToUTC = (localTime) => {
  const date = moment(localTime, 'HH:mm').toDate()
  return date.toISOString().split('T')[1].substring(0, 5);
};

export const convertToGMTLocal = (localTime) => {
  const momentDate = moment.utc(localTime, "HH:mm");
  return momentDate.local().format("HH:mm");
};

const keyDaysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
export const daysOfWeek = () => {
  const locale = getUiLocale();
  const weekdaysObject = {};
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });

  keyDaysOfWeek.forEach((day, index) => {
    const date = new Date(2024, 9, 27 + index);
    weekdaysObject[day] = formatter.format(date);
  });

  return weekdaysObject;
};

export const getDayOfWeek = (dateStr) => {
  if (!dateStr) {
    return dateStr
  }
  const date = new Date(dateStr);
  return keyDaysOfWeek[date.getDay()];
}

export const getCanonicalDayKey = (date) => {
  const m = moment.isMoment(date) ? date : moment(date);
  return keyDaysOfWeek[m.day()];
};
export const slotDefault = { start: '08:00', end: '08:45' };
export const dataTimesDefault = () => {
  const week = daysOfWeek()
  return Object.keys(week).map((key) => (
    { startDate: key, label: week[key], data: [slotDefault], apply: !['sunday', 'saturday'].includes(key) }
  ))
}

function getTargetDate(day, timeString, isUTC = false) {
  const [hours, minutes] = timeString.split(':').map(Number);

  const today = moment().endOf('day');

  const targetDayIndex = keyDaysOfWeek.indexOf(day);
  const todayDayIndex = today.day();
  let daysUntilNextDay = targetDayIndex - todayDayIndex;

  if (daysUntilNextDay < 0) {
    daysUntilNextDay += 7;
  }

  const targetDate = today.clone().add(daysUntilNextDay, 'days');

  if (isUTC) {
    return targetDate.utc().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
  } else {
    return targetDate.local().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
  }
}

export const changeDateLocaleToUTC = (day, timeString) => {
  const originalDate = getTargetDate(day, timeString, false);
  const utcDate = originalDate.clone().utc();
  const originalDateFormat = originalDate.format('YYYY-MM-DD');
  const utcDateFormat = utcDate.format('YYYY-MM-DD');
  return keyDaysOfWeek[utcDate.day()];
};

export const changeDateUTCToLocale = (day, timeString) => {
  const originalDate = getTargetDate(day, timeString, true);

  const localDate = originalDate.clone().local();

  const originalDateFormat = originalDate.format('YYYY-MM-DD');
  const localDateFormat = localDate.format('YYYY-MM-DD');

  if (originalDateFormat !== localDateFormat) {
    return keyDaysOfWeek[localDate.day()];
  }

  return null;
};

export const convertUtcToLocal = (date, time) => {
  const datetime = `${date} ${time}`;
  const utcMoment = moment.utc(datetime, "YYYY-MM-DD HH:mm");
  const localMoment = utcMoment.local();
  return localMoment.format("YYYY-MM-DD");
}

export const convertLocalToUtc = (date, time) => {
  const datetime = `${date} ${time}`;
  const utcMoment = moment(datetime, "YYYY-MM-DD HH:mm");
  const localMoment = utcMoment.utc();
  return localMoment.format("YYYY-MM-DD");
}

export const getTimeRangeUtc = (timeSlotStart, timeSlotEnd) => {
  if (!timeSlotStart || !timeSlotEnd) {
    return {
      timeSlotStart: timeSlotStart,
      timeSlotEnd: timeSlotEnd,
    }
  }
  timeSlotStart = moment(timeSlotStart);
  timeSlotEnd = moment(timeSlotEnd);
  const format = 'DD/MM/YYYY HH:mm:ss';
  let newSlotStart = timeSlotStart.clone().startOf('day');
  let newSlotEnd = timeSlotEnd.clone().endOf('day');
  return {
    timeSlotStart: newSlotStart?.utc()?.format(format),
    timeSlotEnd: newSlotEnd?.utc()?.format(format),
  }
}

export const getTimeRangeLocale = (timeSlotStart, timeSlotEnd) => {
  if (!timeSlotStart || !timeSlotEnd) {
    return {
      timeSlotStart: timeSlotStart,
      timeSlotEnd: timeSlotEnd,
    }
  }

  let newSlotStart = moment.utc(timeSlotStart);
  let newSlotEnd = moment.utc(timeSlotEnd);

  return {
    timeSlotStart: newSlotStart?.local(),
    timeSlotEnd: newSlotEnd?.local(),
  }
}

export const mapDateTimeToUtc = (dataTimes) => {
  let dataTime = cloneDeep(dataTimes)
  if (!dataTime || !dataTime.length) {
    return dataTime
  }
  const dateNeedAdd = {}

  dataTime.forEach(date => {
    if (!date.apply) {
      return
    }
    const slotNeedSubtract = []

    date.data.forEach((slots) => {
      const dateInWeek = changeDateLocaleToUTC(date.startDate, slots.start);
      if (!dateInWeek) {
        return
      }

      slotNeedSubtract.push(slots.start)
      if (dateNeedAdd[dateInWeek]) {
        dateNeedAdd[dateInWeek].push(slots)
      } else {
        dateNeedAdd[dateInWeek] = [slots]
      }
    })

    if (slotNeedSubtract.length) {
      date.data = date.data.filter(slots => !slotNeedSubtract.includes(slots.start))
    }

    if (!date.data.length) {
      date.apply = false
    }
  })

  if (Object.keys(dateNeedAdd).length) {
    dataTime = dataTime.map(date => {
      const newDate = {...date}
      const {startDate} = newDate
      if (dateNeedAdd[startDate]) {
        if (!newDate.apply) {
          newDate.apply = true
          newDate.data = []
        }
        newDate.data = newDate.data.concat(dateNeedAdd[startDate]);
      }
      if (newDate.apply) {
        return {
          startDate: startDate,
          data: newDate.data.map(slot => {
            const newSlot = {
              start: convertToUTC(slot.start),
              end: convertToUTC(slot.end)
            }
            if (slot.startTimeOneSlot) {
              newSlot.startTimeOneSlot = convertToUTC(slot.startTimeOneSlot);
              newSlot.endTimeOneSlot = convertToUTC(slot.endTimeOneSlot);
              newSlot.startTimeTopicOneSlot = slot.startTimeTopicOneSlot
              newSlot.endTimeTopicOneSlot = slot.endTimeTopicOneSlot
            }
            return newSlot
          }),
        }
      }
      return null
    }).filter(item => item)
  } else {
    dataTime = dataTime.filter(item => item.apply).map((day) => ({
      startDate: day.startDate,
      data: day.data.map(slot => {
        const newSlot = {
          start: convertToUTC(slot.start),
          end: convertToUTC(slot.end)
        }
        if (slot.startTimeOneSlot) {
          newSlot.startTimeOneSlot = convertToUTC(slot.startTimeOneSlot);
          newSlot.endTimeOneSlot = convertToUTC(slot.endTimeOneSlot);
          newSlot.startTimeTopicOneSlot = slot.startTimeTopicOneSlot
          newSlot.endTimeTopicOneSlot = slot.endTimeTopicOneSlot
        }
        return newSlot
      }),
    }));
  }

  return dataTime
}
